<template>
    <div class="tool-perm" :class="`tool-perm-${state}`">
        <div class="tool-perm-head">
            <span class="tool-perm-icon" aria-hidden="true">
                <i class="fa-solid" :class="headIcon"></i>
            </span>
            <div class="tool-perm-title">
                <p class="tool-perm-heading mb-0">{{ heading }}</p>
                <p class="tool-perm-sub mb-0">
                    <span class="tool-perm-server">{{ record.serverName || 'External server' }}</span>
                    <span class="tool-perm-sep">·</span>
                    <code>{{ record.rawToolName || record.toolName }}</code>
                </p>
            </div>
        </div>

        <p v-if="record.description" class="tool-perm-desc mb-0">{{ record.description }}</p>

        <!-- What it will be called with. Collapsed by default: the arguments are
             the detail that decides a borderline case, but they are noise on the
             many calls where the tool name is answer enough. -->
        <div v-if="hasInput" class="tool-perm-args">
            <button type="button" class="tool-perm-args-toggle" @click="showInput = !showInput">
                <i class="fa-solid me-1" :class="showInput ? 'fa-angle-down' : 'fa-angle-right'" aria-hidden="true"></i>
                {{ showInput ? 'Hide' : 'Show' }} what it will be called with
            </button>
            <pre v-if="showInput" class="tool-perm-args-body">{{ prettyInput }}</pre>
        </div>

        <p v-if="state === 'pending'" class="tool-perm-scope mb-0">
            Your answer covers this one call. Which tools ask for permission is
            set for the whole client under PAICE settings.
        </p>

        <div v-if="state === 'pending'" class="tool-perm-actions">
            <button type="button" class="btn btn-primary btn-sm" :disabled="!!submitting || disabled"
                @click="decide('allow_once')">
                <span v-if="submitting === 'allow_once'" class="spinner-border spinner-border-sm me-1" role="status"
                    style="width:1em;height:1em"><span class="visually-hidden">Sending</span></span>
                Allow
            </button>
            <button type="button" class="btn btn-outline-danger btn-sm" :disabled="!!submitting || disabled"
                @click="decide('deny')">
                <span v-if="submitting === 'deny'" class="spinner-border spinner-border-sm me-1" role="status"
                    style="width:1em;height:1em"><span class="visually-hidden">Sending</span></span>
                Deny
            </button>
        </div>

        <!-- An answer covers this one call and nothing else, so there is
             nothing to report beyond what happened to it. -->
        <p v-else class="tool-perm-outcome mb-0">{{ outcomeText }}</p>

        <p v-if="error" class="tool-perm-error mb-0">
            <i class="fa-solid fa-circle-exclamation me-1"></i>{{ error }}
        </p>
    </div>
</template>

<script>
/**
 * The in-chat approval prompt for one external MCP tool call.
 *
 * Purely presentational plus one emit: the parent owns the request that
 * releases the waiting stream, because it also owns the message the record
 * lives on. This component never mutates `record` — the parent writes the
 * outcome back, and the same record is what gets persisted with the message, so
 * reopening the chat shows the same card in the same state.
 */
const HEADINGS = {
    allowed: 'You allowed this tool',
    denied: 'You denied this tool',
    timed_out: 'The request timed out',
    not_prompted: 'This tool was not run',
};

export default {
    name: 'McpToolPermissionPrompt',
    props: {
        /** A `toolPermissions` entry: the SSE payload plus, once answered, its outcome. */
        record: { type: Object, required: true },
        /** Disables the buttons — set while the chat is not connected to a stream. */
        disabled: { type: Boolean, default: false },
        /**
         * Sends the answer; resolves when it has landed, rejects with something
         * worth showing when it has not.
         *
         * A prop rather than an emit on purpose: the stream is blocked until the
         * write succeeds, so this component has to be able to await it and show
         * a failure. `$emit` returns the component instance, not the handler's
         * promise, so an emitted answer could only ever be fire-and-forget.
         */
        submit: { type: Function, required: true },
    },
    data() {
        return {
            showInput: false,
            /** The decision currently in flight, so only its own button spins. */
            submitting: null,
            error: '',
        };
    },
    computed: {
        /**
         * `pending` is the only interactive state. A record with no outcome is
         * one the user has not answered — including after a reload, where the
         * stream is long gone; the parent passes `disabled` for that case rather
         * than this component having to know about streams.
         */
        state() {
            return this.record.outcome || 'pending';
        },
        heading() {
            if (this.state === 'pending') {
                return `Allow ${this.record.title || this.record.rawToolName || this.record.toolName}?`;
            }
            return HEADINGS[this.state] || 'This tool was not run';
        },
        headIcon() {
            return {
                pending: 'fa-hand',
                allowed: 'fa-circle-check',
                denied: 'fa-ban',
                timed_out: 'fa-clock',
                not_prompted: 'fa-circle-exclamation',
            }[this.state] || 'fa-circle-exclamation';
        },
        outcomeText() {
            return {
                allowed: 'PAICE ran it and used the result.',
                denied: 'PAICE did not run it and was told to carry on without it.',
                timed_out: 'It was not answered in time, so the tool did not run. Ask again to retry.',
                not_prompted: 'The request could not be shown, so nothing ran. Send the message again.',
            }[this.state] || '';
        },
        hasInput() {
            return !!this.record.input && Object.keys(this.record.input).length > 0;
        },
        prettyInput() {
            try {
                return JSON.stringify(this.record.input, null, 2);
            } catch (e) {
                return String(this.record.input);
            }
        },
    },
    methods: {
        /**
         * Hand the answer to the parent and wait for it to land. The stream is
         * blocked until the write succeeds, so a failure has to be shown here
         * and the buttons left live — silently swallowing it would leave the
         * user watching a chat that never moves.
         */
        async decide(decision) {
            if (this.submitting || this.disabled) return;
            this.submitting = decision;
            this.error = '';
            try {
                await this.submit({ record: this.record, decision });
            } catch (e) {
                this.error = e?.message || 'Could not send your answer. Try again.';
            } finally {
                this.submitting = null;
            }
        },
    },
};
</script>

<style scoped>
/* Deliberately unlike the surrounding message bubbles: this is a question the
   chat is blocked on, not more output. The left rail carries the state, in the
   same colours the settings page uses for allow / ask / deny. */
.tool-perm {
    position: relative;
    padding: 0.85rem 1rem 0.85rem 1.15rem;
    border: 1px solid #edf2f9;
    border-radius: 0.5rem;
    background: #f9fbfd;
    overflow: hidden;
}

.tool-perm::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 3px;
}

.tool-perm-pending::before {
    background: #d9822b;
}

.tool-perm-allowed::before {
    background: #00a854;
}

.tool-perm-denied::before,
.tool-perm-not_prompted::before {
    background: #e63757;
}

.tool-perm-timed_out::before {
    background: #95aac9;
}

.tool-perm-head {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
}

.tool-perm-icon {
    flex-shrink: 0;
    font-size: 0.95rem;
    line-height: 1.4;
    color: #95aac9;
}

.tool-perm-pending .tool-perm-icon {
    color: #d9822b;
}

.tool-perm-allowed .tool-perm-icon {
    color: #00a854;
}

.tool-perm-denied .tool-perm-icon,
.tool-perm-not_prompted .tool-perm-icon {
    color: #e63757;
}

.tool-perm-title {
    min-width: 0;
}

.tool-perm-heading {
    font-size: 0.9rem;
    font-weight: 600;
    color: #12263f;
}

.tool-perm-sub {
    font-size: 0.72rem;
    color: #95aac9;
    word-break: break-word;
}

.tool-perm-server {
    font-weight: 600;
}

.tool-perm-sep {
    margin: 0 0.35rem;
}

.tool-perm-sub code {
    font-size: 0.72rem;
    color: #6e84a3;
}

.tool-perm-desc {
    margin-top: 0.5rem;
    font-size: 0.8rem;
    color: #6e84a3;
}

.tool-perm-args {
    margin-top: 0.5rem;
}

.tool-perm-args-toggle {
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 0.72rem;
    font-weight: 600;
    color: #2c7be5;
}

.tool-perm-args-body {
    margin: 0.35rem 0 0;
    padding: 0.5rem 0.6rem;
    max-height: 12rem;
    overflow: auto;
    border-radius: 0.375rem;
    background: #edf2f9;
    font-size: 0.72rem;
    color: #12263f;
    white-space: pre-wrap;
    word-break: break-word;
}

.tool-perm-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.75rem;
}

.tool-perm-outcome {
    margin-top: 0.5rem;
    font-size: 0.78rem;
    color: #6e84a3;
}

.tool-perm-scope {
    margin-top: 0.5rem;
    font-size: 0.72rem;
    color: #95aac9;
}

.tool-perm-error {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: #e63757;
}
</style>

<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

/* Unscoped: `body.dark-mode` is set on an ancestor outside this component, so a
   scoped selector could never match it — and `:global(body.dark-mode) .tool-perm`
   does not work either, because Vue's scoped-CSS transform keeps only what is
   inside `:global()` and drops the rest of the selector, so those rules were
   painting `body` and never the card. Same shape as the dark block in
   McpServers.vue, which owns the settings-side view of the same permissions. */
body.dark-mode {

    /* The chat renders on a near-black surface; the card has to follow or it
       becomes the brightest thing on the page. Recessed rather than raised — the
       message bubbles beside it are the elevated surface, and the rail plus the
       pending wash are what make this read as a question, not more output. */
    .tool-perm {
        border-color: $dark-border-color !important;
        background: $dark-card-bg !important;
    }

    /* Light mode leans on the rail alone; on dark a rail 3px wide is not enough
       to mark the one card the chat is blocked on, so pending also gets a wash. */
    .tool-perm-pending {
        border-color: rgba(217, 130, 43, 0.35) !important;
        background: rgba(217, 130, 43, 0.07) !important;
    }

    .tool-perm-heading {
        color: $dark-text-primary !important;
    }

    .tool-perm-sub,
    .tool-perm-sub code,
    .tool-perm-desc,
    .tool-perm-outcome,
    .tool-perm-scope {
        color: $dark-text-secondary !important;
    }

    .tool-perm-icon {
        color: $dark-text-secondary !important;
    }

    /* State colours lifted to the same dark-surface variants McpServers.vue uses
       for its status pills — the two views describe the same allow / ask / deny,
       so they should not disagree on the shade. */
    .tool-perm-pending .tool-perm-icon {
        color: #e0a458 !important;
    }

    .tool-perm-allowed .tool-perm-icon {
        color: #4fd18a !important;
    }

    .tool-perm-denied .tool-perm-icon,
    .tool-perm-not_prompted .tool-perm-icon {
        color: #f08a9c !important;
    }

    .tool-perm-allowed::before {
        background: #4fd18a !important;
    }

    .tool-perm-denied::before,
    .tool-perm-not_prompted::before {
        background: #f08a9c !important;
    }

    .tool-perm-timed_out::before {
        background: #4a5361 !important;
    }

    .tool-perm-args-toggle {
        color: #7ab0ff !important;
    }

    /* Bordered as well as filled: an unbordered dark block on a dark card loses
       its edge, and the arguments are the part the user is meant to read. */
    .tool-perm-args-body {
        background: $dark-body-bg !important;
        border: 1px solid $dark-border-color !important;
        color: $dark-text-primary !important;
    }

    /* Bootstrap's primary/danger outlines are mixed for contrast against a
       near-black fill, so the actions get the brighter accents used elsewhere in
       the chat. `btn-primary` stays as-is — a solid fill is legible already. */
    .tool-perm-actions .btn-outline-danger {
        color: #f08a9c !important;
        border-color: rgba(240, 138, 156, 0.55) !important;
    }

    .tool-perm-actions .btn-outline-danger:hover:not(:disabled) {
        background: rgba(240, 138, 156, 0.14) !important;
        border-color: #f08a9c !important;
        color: #f7a9b7 !important;
    }

    .tool-perm-error {
        color: #f08a9c !important;
    }
}
</style>
